/**
 * The authorization surface, in one file.
 *
 * `MODULE_PERMISSIONS` is the source of truth for every permission string in the
 * system. `ModuleKey` and `PermissionKey` are *derived* from that object literal
 * rather than maintained alongside it, so a typo in a nav guard, a route guard or
 * the seed script is a compile error instead of a silent 403.
 *
 * The seed script reads `ALL_PERMISSIONS` and reconciles the `Permission` table
 * against it — adding a module here and re-seeding creates the rows; removing one
 * prunes them. Never hand-edit `Permission` rows.
 */

export const ACTIONS = [
  'view',
  'create',
  'update',
  'delete',
  'export',
  'import',
  'approve',
  'assign',
  'manage',
  // Bridal-domain verbs the generic CRUD set cannot express.
  'return', // hand a rented item back and close its booking
  'settle', // resolve a deposit or a buyback into a final amount
  'cancel', // reverse a confirmed sale / void a quotation
  'confirm', // commit a reserved rental booking to a firm commitment
  'dispatch', // send a hired piece out of the showroom
  'deliver', // record that the customer physically received it
  'convert', // turn an accepted quotation into a sale
  'complete', // take ownership of a bought-back piece and close the document
  'print', // render an invoice / document
  'reverse', // reverse a completed payment (never delete it)
  'post', // commit a draft journal to the ledger, after which it is immutable
  'close', // close a fiscal period, after which nothing may post into it
  'reply', // answer a customer in the social inbox (WhatsApp, Instagram, Messenger)
] as const;

export type ActionKey = (typeof ACTIONS)[number];

const CRUD = ['view', 'create', 'update', 'delete'] as const;

/**
 * The permission catalog.
 *
 * Declaring a module here creates its permission rows on the next `db:seed`. It
 * does *not* create a screen, a route, or a nav entry — those live in the module
 * registry (`packages/shared/src/modules.ts`) and are added when the module is
 * actually built. The two are intentionally independent: a permission can exist
 * before the module that consumes it, which is exactly what Phase 1 needs —
 * the authorization surface for the whole bridal domain, ahead of any feature.
 */
export const MODULE_PERMISSIONS = {
  // ---- Platform / administration ------------------------------------------
  dashboard: ['view', 'export'],
  users: [...CRUD, 'export', 'manage'],
  roles: [...CRUD],
  audit_logs: ['view', 'export'],
  // Customer communication (Phase 7).
  //
  // Three verbs, and the two that are absent are the point. There is no
  // `notifications:send`, because this phase has no arbitrary-send endpoint:
  // a notification exists because a business transition succeeded, so the
  // right to send one IS the right to confirm the sale or take the payment.
  // There is no `notifications:retry` either — retrying a failed delivery is
  // an implementation detail of operating the queue, and a permission that
  // would be granted with `manage` everywhere and withheld nowhere means
  // nothing. That is the Phase 5 lesson about `evaluate`, applied again.
  //
  //   `view`    read the notification centre, the delivery log and the reports.
  //   `manage`  operate it: retry a failed delivery, run the processor, cancel
  //             a pending one, and author organisation templates.
  //   `export`  take the delivery log out as a file. Distinct from `view`
  //             for the reason every other export is: a file leaves with
  //             whoever downloaded it, carrying customer names and numbers.
  notifications: ['view', 'manage', 'export'],
  company_settings: ['view', 'update', 'manage'],
  // Lead capture & social inbox.
  //
  //   integrations:view    see which sources are connected and their event log.
  //   integrations:manage  connect a website form, a Facebook Page or a WhatsApp
  //                        number, paste its tokens, disable it. Tokens are real
  //                        credentials, so this is an administrator's right.
  integrations: ['view', 'manage'],
  //   inbox:view    read WhatsApp / Instagram / Messenger conversations.
  //   inbox:reply   send a message to the customer from the ERP. Split from
  //                 view because a reply is sent in the business's name.
  //   inbox:manage  assign and close conversations, link them to leads.
  inbox: ['view', 'reply', 'manage'],

  // ---- CRM ----------------------------------------------------------------
  customers: [...CRUD, 'export'],
  leads: [...CRUD, 'assign', 'export'],
  lead_sources: [...CRUD],
  followups: [...CRUD],
  appointments: [...CRUD],

  // ---- Catalogue & inventory ----------------------------------------------
  categories: [...CRUD, 'manage'],
  brands: [...CRUD],
  collections: [...CRUD],
  products: [...CRUD, 'export', 'import', 'manage'],
  product_items: [...CRUD, 'export', 'manage'],
  // Inventory movements/history are an append-only ledger — adjust, never delete.
  inventory: ['view', 'create', 'update', 'export', 'manage'],
  // Physical inventory locations within a branch (Showroom, Warehouse, Repair…).
  inventory_locations: [...CRUD, 'manage'],

  // ---- Sales, rentals, buyback --------------------------------------------
  quotations: [...CRUD, 'approve', 'convert', 'cancel', 'manage'],
  sales: [...CRUD, 'cancel', 'export', 'manage'],
  // Invoices are a view/print surface over confirmed sales, not their own entity.
  invoices: ['view', 'print', 'manage'],
  // One permission namespace covers the whole rental chain — booking,
  // agreement, dispatch and return — because they are one business document
  // flowing through states, not four modules. `confirm` commits a reserved
  // booking; `dispatch` sends the piece out (and is what moves inventory to
  // RENTED); `deliver` records customer receipt; `return` confirms the pieces
  // are back (and is what moves inventory out of RENTED); `settle` takes and
  // refunds deposit money; `cancel` releases held inventory; `print` renders
  // the agreement, dispatch note and return report; `manage` grants org-wide
  // visibility (record-level checks scope the rest).
  //
  // `return` and `settle` are separate because they are different jobs: the
  // showroom floor takes the garments back, the finance desk hands the money
  // over. Splitting them lets a seller close the physical loop without gaining
  // the right to release a deposit.
  rentals: [
    ...CRUD,
    'confirm',
    'dispatch',
    'deliver',
    'return',
    'settle',
    'cancel',
    'print',
    'export',
    'manage',
  ],
  // Buying a physical piece back from the customer who owns it (Phase 5).
  //
  // The verbs follow the separation of duties the showroom actually has, and
  // reuse the existing vocabulary rather than inventing a synonym for it:
  //
  //   `update`   records — and corrects — the evaluation. Evaluating is editing
  //              a draft, not a distinct authority: any role trusted to raise a
  //              buyback is trusted to write down what the garment looks like,
  //              so a separate `evaluate` key would be granted with `update`
  //              everywhere and mean nothing.
  //   `approve`  clears an amount. The manager's decision, withheld from the
  //              seller who proposed it — the same split as quotations.
  //   `complete` takes the piece into stock and freezes what we owe. It is the
  //              irreversible one, so it is its own right, exactly as
  //              `rentals:dispatch` and `rentals:return` are.
  //   `settle`   records a payout against a completed buyback. Finance, not the
  //              showroom floor — the same reason `rentals:settle` exists.
  //   `cancel`   abandons an open document; `print` renders the buyback note;
  //              `export` takes the figures out as a file; `manage` grants
  //              org-wide visibility (record-level checks scope the rest).
  buybacks: [...CRUD, 'approve', 'complete', 'settle', 'cancel', 'print', 'export', 'manage'],
  // `reverse` voids a completed payment without deleting it; `manage` grants
  // org-wide visibility of every payment (record-level checks scope the rest).
  payments: [...CRUD, 'export', 'reverse', 'manage'],
  // Receipts are a view/print surface over payments, not their own entity.
  receipts: ['view', 'print'],
  reports: ['view', 'export'],

  // ---- Finance & accounting (Phase 8) --------------------------------------
  //
  // Eight verbs, and the shape of the list is the segregation of duties a
  // finance function actually has. What is absent matters as much as what is
  // present: there is no `delete`, because a posted journal is never deleted and
  // discarding a draft is the same authority as typing one; and there is no
  // separate `update`, for the same reason — drafting, editing and abandoning a
  // draft journal are one job, not three. That is the Phase 5 `evaluate` lesson
  // and the Phase 7 `retry` lesson applied a third time.
  //
  //   `view`     read the chart, the periods, the journals, the ledger and every
  //              report. Accounting has no per-user record ownership: a journal
  //              belongs to the organisation, not to whoever typed it, so this
  //              is org-wide by construction and needs no `manage` beside it.
  //   `create`   draft, edit and discard a manual journal. Nothing has entered
  //              the ledger yet, so this is the least dangerous of the write
  //              rights and the one a junior bookkeeper gets.
  //   `post`     commit a journal to the ledger. The irreversible one, and the
  //              reason it is split from `create`: the person who types an entry
  //              and the person who stands behind it need not be the same.
  //   `reverse`  post the mirror image of an entry that is already history. Not
  //              folded into `post`, because reversing somebody else's posted
  //              journal is precisely the authority a control system exists to
  //              hold apart from raising one.
  //   `close`    close a fiscal period. After it, nothing posts into that window
  //              again — the strongest single action in the module.
  //   `export`   take a ledger or a statement out as a file. Distinct from
  //              `view` for the reason every other export is: a file leaves with
  //              whoever downloaded it, and this one carries the whole financial
  //              position of the business.
  //   `print`    render a journal or a statement as a paper document. Held apart
  //              from `export` because the two leak differently: an export is a
  //              machine-readable file that travels, a print is a signed sheet
  //              somebody files. A showroom manager who may hand an auditor a
  //              printed trial balance need not also be able to download the
  //              whole ledger as CSV.
  //   `manage`   maintain the chart of accounts and open fiscal periods. Master
  //              data, not transactions — the one right that changes what the
  //              ledger is *made of* rather than what is in it.
  accounting: ['view', 'create', 'post', 'reverse', 'close', 'export', 'print', 'manage'],

  // ---- Procurement & accounts payable (Phase 11) ---------------------------
  //
  // Three namespaces, and the split follows what the three jobs actually are:
  // maintaining who the suppliers are, raising and posting the documents, and
  // reading what the business owes.
  //
  // `vendors` has no `delete`, on purpose and for the same reason `customers`
  // would not want one: a vendor names purchases, and purchases name journals.
  // Removing the row would orphan financial history. Deactivation is what the
  // business actually means by "we do not buy from them any more", and it is
  // what `update` already expresses.
  vendors: ['view', 'create', 'update', 'manage', 'export'],
  // The verbs mirror the sales document exactly, because a purchase *is* the
  // mirror of a sale — raised, posted, cancelled, printed, exported.
  //
  //   `post`    the irreversible one. It receives the stock, establishes the
  //             cost, moves the ledger and creates the liability, so it is its
  //             own right rather than folded into `create` — the same split as
  //             `accounting:post` and `buybacks:complete`.
  //   `cancel`  abandons a draft. It cannot touch a posted document at all, so
  //             it is the least dangerous verb here despite the name.
  //   `manage`  org-wide visibility of every purchase (record-level checks
  //             scope the rest), matching how `sales:manage` behaves.
  //   `reverse` (Phase 12) undoes a posting that should not have happened. A
  //             finance correction, held apart from `post` the way
  //             `accounting:reverse` is held apart from `accounting:post`.
  purchases: ['view', 'create', 'update', 'post', 'cancel', 'print', 'export', 'manage', 'reverse'],
  // Handing goods back to a vendor (Phase 12).
  //
  //   `approve`  clears a return for completion. Its own verb so the person
  //              who raised the return need not be the person who agrees it
  //              should reduce what the business owes — the buybacks split.
  //   `complete` the economic event: stock leaves, the ledger moves, the
  //              payable falls or a credit is raised.
  //   `cancel`   abandons a draft or an approval. Cannot touch a completed
  //              return, which is history.
  // No `delete`: a completed return is never deleted, and a draft is cancelled
  // so what was intended stays readable.
  purchase_returns: ['view', 'create', 'update', 'approve', 'complete', 'cancel', 'export'],
  // Paying a supplier, and reading what is owed.
  //
  //   `view`    the payables summary, the aging and a vendor statement.
  //   `settle`  record a payment against one or more purchases. The existing
  //             verb, not a new `pay`: `buybacks:settle` already means "records
  //             a payout against a completed document", which is exactly this
  //             on the other party. Held apart from `purchases:post` because
  //             the person who receives the stock and the person who releases
  //             the money must not have to be the same — that separation is the
  //             only reason this namespace is worth having.
  //   `reverse` void a completed vendor payment without deleting it. The same
  //             authority `payments:reverse` is, on the other side of the books.
  //   `export`  take the payables out as a file. Distinct from `view` for the
  //             reason every other export is: the file carries the whole
  //             supplier position of the business and leaves with whoever
  //             downloaded it.
  payables: ['view', 'settle', 'reverse', 'export'],

  // ---- Customer portal -----------------------------------------------------
  // Deliberately a single, portal-only permission. A customer must never hold an
  // internal ERP permission; portal access plus record-level ownership checks
  // (see docs/AUTHORIZATION.md) are what scope them to their own data.
  portal: ['view'],
} as const satisfies Record<string, readonly ActionKey[]>;

export type ModuleKey = keyof typeof MODULE_PERMISSIONS;

export type PermissionKey = {
  [M in ModuleKey]: `${M}:${(typeof MODULE_PERMISSIONS)[M][number]}`;
}[ModuleKey];

export const MODULE_KEYS = Object.keys(MODULE_PERMISSIONS) as ModuleKey[];

export const ALL_PERMISSIONS: PermissionKey[] = MODULE_KEYS.flatMap((moduleKey) =>
  MODULE_PERMISSIONS[moduleKey].map((action) => `${moduleKey}:${action}` as PermissionKey),
);

/** Type-safe permission constructor — `permissionKey('users', 'create')`. */
export const permissionKey = <M extends ModuleKey>(
  moduleKey: M,
  action: (typeof MODULE_PERMISSIONS)[M][number],
): PermissionKey => `${moduleKey}:${action}` as PermissionKey;

export const SYSTEM_ROLES = ['super_admin', 'admin', 'viewer'] as const;
export type SystemRoleSlug = (typeof SYSTEM_ROLES)[number];

/** One module's slice of the permission matrix, as the role editor renders it. */
export interface PermissionCatalogEntry {
  key: PermissionKey;
  action: string;
  label: string;
}

export interface PermissionCatalogGroup {
  module: string;
  label: string;
  /** Nav group the module belongs to, so the matrix reads like the sidebar. */
  group: string;
  permissions: PermissionCatalogEntry[];
}
